import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subscription, firstValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SurveyHeroComponent } from '../../components/survey-hero/survey-hero.component';
import { SurveyQuestionComponent } from '../../components/survey-question/survey-question.component';
import { SurveyPublicReviewPreviewComponent } from '../../components/survey-public-review-preview/survey-public-review-preview.component';
import {
  SurveyAnswerValue,
  SurveyConfigResponse,
  SurveyLocalizedText,
  SurveyPage,
  SurveyQuestion,
  SurveyScale,
  SurveySection,
  SurveySectionAnswers,
  SurveySubmitAnswers,
  SurveyTripPhaseContext,
  SurveyTripPhaseSectionAnswers,
} from '../../models/survey.models';

@Component({
  selector: 'app-survey-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatProgressBarModule,
    SurveyHeroComponent,
    SurveyQuestionComponent,
    SurveyPublicReviewPreviewComponent,
  ],
  templateUrl: './survey-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './survey-page.component.scss',
})
export class SurveyPageComponent implements OnInit, OnDestroy {
  protected pages = signal<SurveyPage[]>([]);
  protected currentPageIndex = signal(0);
  protected selectedLocale = signal<'fi' | 'en'>(this.getPreferredLocale());
  protected currentPage = computed(() => {
    const items = this.pages();
    const index = this.currentPageIndex();
    return items[index] ?? null;
  });
  protected firstName = signal<string | null>(null);
  protected loading = signal(true);
  protected error = signal<string | null>(null);
  protected submitError = signal<string | null>(null);
  protected submitted = signal(false);
  protected submitting = signal(false);
  protected surveyForm = new FormGroup({});
  private formValueSubscription: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
  ) {}

  async ngOnInit() {
    if (this.route.snapshot.data['demo']) {
      const demoPages = this.buildDemoPages();
      this.pages.set(demoPages);
      this.buildForm(demoPages);
      this.currentPageIndex.set(0);
      this.loading.set(false);
      return;
    }

    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.error.set('Survey link is invalid.');
      this.loading.set(false);
      return;
    }

    try {
      const apiBaseUrl = this.getApiBaseUrl();
      const response = await firstValueFrom(
        this.http.get<SurveyConfigResponse>(
          `${apiBaseUrl}/api/v2/public/feedback-surveys/${token}`,
        ),
      );

      if (response.isCompleted || !response.canSubmit) {
        this.submitted.set(true);
        return;
      }

      const pages = response.pages || [];
      this.pages.set(pages);
      this.firstName.set(response.firstName ?? null);
      this.buildForm(pages);
      this.currentPageIndex.set(0);
    } catch (error) {
      this.error.set('Unable to load the survey. Please try again later.');
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.formValueSubscription?.unsubscribe();
    this.formValueSubscription = null;
  }

  async submit() {
    if (this.loading() || this.error() || this.submitting()) {
      return;
    }

    this.submitError.set(null);

    const hasMissingRequiredQuestions = this.markMissingRequiredQuestionsAsTouched(this.pages());
    if (hasMissingRequiredQuestions) {
      const missingRequiredQuestion = this.findFirstMissingRequiredQuestion(this.pages());
      if (missingRequiredQuestion) {
        this.currentPageIndex.set(missingRequiredQuestion.pageIndex);
      }
      this.submitError.set(this.uiText('requiredBeforeSubmit'));
      return;
    }

    if (this.route.snapshot.data['demo']) {
      this.submitted.set(true);
      return;
    }

    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.error.set('Survey link is invalid.');
      return;
    }

    this.submitting.set(true);
    try {
      const apiBaseUrl = this.getApiBaseUrl();
      await firstValueFrom(
        this.http.post(`${apiBaseUrl}/api/v2/public/feedback-surveys/${token}/answers`, {
          answers: this.getAnswersForSubmit(),
        }),
      );
      this.submitted.set(true);
    } catch (error) {
      if (this.isAlreadyCompletedError(error)) {
        // The answers are already stored, so this is a success from the customer's side.
        // Happens when a tab left open on the survey is restored and sent again.
        this.submitted.set(true);
        return;
      }

      const message = this.getSubmitErrorMessage(error);
      this.submitError.set(message ?? this.uiText('submitFailed'));
    } finally {
      this.submitting.set(false);
    }
  }

  private isAlreadyCompletedError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const body = (error as { error?: { code?: unknown } }).error;
    return body?.code === 'SURVEY_ALREADY_COMPLETED';
  }

  private getApiBaseUrl() {
    return environment.surveyApiBaseUrl.replace(/\/$/, '');
  }

  protected getScaleValues(question: SurveyQuestion): number[] {
    const scale = question.scale ?? this.getDefaultScale(question.type);
    if (!scale) {
      return [];
    }

    const values: number[] = [];
    for (let value = scale.min; value <= scale.max; value += scale.step) {
      values.push(value);
    }
    return values;
  }

  protected shouldShowQuestion(section: SurveySection, question: SurveyQuestion): boolean {
    const conditional = question.conditional ?? this.getDefaultQuestionCondition(section, question);
    if (!conditional) {
      return true;
    }

    const answer =
      this.getRawAnswer(section, conditional.whenQuestionId) ??
      this.findAnswerByQuestionId(conditional.whenQuestionId);

    if (answer === undefined || answer === null || answer === '') {
      return false;
    }

    const conditionValue = conditional.value;
    const operator = conditional.operator;

    if (typeof conditionValue === 'number') {
      const numericAnswer = Number(answer);
      if (Number.isNaN(numericAnswer)) {
        return false;
      }

      switch (operator) {
        case 'lt':
          return numericAnswer < conditionValue;
        case 'lte':
          return numericAnswer <= conditionValue;
        case 'gt':
          return numericAnswer > conditionValue;
        case 'gte':
          return numericAnswer >= conditionValue;
        case 'eq':
          return numericAnswer === conditionValue;
        case 'neq':
          return numericAnswer !== conditionValue;
        default:
          return false;
      }
    }

    switch (operator) {
      case 'eq':
        return answer === conditionValue;
      case 'neq':
        return answer !== conditionValue;
      default:
        return false;
    }
  }

  private getDefaultQuestionCondition(
    section: SurveySection,
    question: SurveyQuestion,
  ): SurveyQuestion['conditional'] | null {
    if (
      section.id !== 'nps' ||
      (question.id !== 'nps_followup_feedback' && question.id !== 'nps_followup_contact')
    ) {
      return null;
    }

    return { whenQuestionId: 'nps_score', operator: 'lte', value: 6 };
  }

  protected text(value?: SurveyLocalizedText | string): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (this.selectedLocale() === 'en' && value.en) {
      return value.en;
    }

    return value.fi || value.en || '';
  }

  protected requiredIndicatorText(question?: SurveyQuestion): string {
    return this.uiText('requiredIndicator');
  }

  protected uiText(
    key:
      | 'heroEyebrow'
      | 'heroTitle'
      | 'heroSubtitle'
      | 'heroSubtitleFirstPage'
      | 'sectionLabel'
      | 'backButton'
      | 'nextButton'
      | 'submitButton'
      | 'thankYouTitle'
      | 'thankYouBody'
      | 'requiredIndicator'
      | 'textareaPlaceholder'
      | 'languageLabel'
      | 'requiredBeforeSubmit'
      | 'submitFailed'
      | 'requiredBeforeContinue'
      | 'npsScaleLegend'
      | 'privacyNotice',
  ): string {
    const locale = this.selectedLocale();
    const texts = {
      fi: {
        heroEyebrow: 'IMT palaute',
        heroTitle: 'Palautteesi matkasta',
        heroSubtitle: '',
        heroSubtitleFirstPage: `<b>Kiitos, että matkustit IMT:n kanssa.</b> Tervetuloa takaisin kotiin!

Kerro meille, miten matkasi sujui. Mikä onnistui erityisen hyvin ja missä voisimme parantaa?

Jokainen palaute auttaa meitä palvelemaan asiakkaitamme entistä paremmin.

Vastaamalla kyselyyn osallistut kuukausittaiseen <b>100 € matkalahjakortin</b> arvontaan.`,
        sectionLabel: 'Osio',
        backButton: 'Takaisin',
        nextButton: 'Seuraava',
        submitButton: 'Lähetä palaute',
        thankYouTitle: 'Kiitos matkasta kanssamme! ❤️',
        thankYouBody: `Palautteesi on vastaanotettu ja auttaa meitä luomaan entistä parempia matkakokemuksia.<br><br>🎁 Olet mukana 100 € matkalahjakortin arvonnassa.<br><br>Toivottavasti näemme sinut pian uudella matkalla.`,
        requiredIndicator: 'Pakollinen',
        textareaPlaceholder: 'Kerro ajatuksesi...',
        languageLabel: 'Kieli',
        requiredBeforeSubmit: 'Vastaa kaikkiin pakollisiin kysymyksiin ennen lähettämistä.',
        submitFailed: 'Palautteen lähettäminen ei onnistunut. Yritä hetken kuluttua uudelleen.',
        requiredBeforeContinue: 'Vastaa kaikkiin pakollisiin kysymyksiin ennen jatkamista.',
        npsScaleLegend: '0 = en suosittelisi, 10 = todennäköisin',
        privacyNotice:
          'Lähettämällä palautteen hyväksyt IMT:n <a href="https://www.imt.fi/rekisteri-ja-tietosuojaseloste/" target="_blank" rel="noopener noreferrer">tietosuojaselosteen</a>.',
      },
      en: {
        heroEyebrow: 'IMT feedback',
        heroTitle: 'Your trip feedback',
        heroSubtitle: '',
        heroSubtitleFirstPage: `Thank you for traveling with IMT. We hope you had a great trip. Welcome home!

We are interested in hearing how your trip went. What went well and where can we improve to serve you better?

Your answers, thoughts, feedback and ideas are our most important tool in making our holiday trips even better! Completing the survey takes about 5-10 minutes.

<b>By responding, you will be entered into a monthly draw for a 100 EUR travel voucher.</b>

Thank you for your help!`,
        sectionLabel: 'Section',
        backButton: 'Back',
        nextButton: 'Next',
        submitButton: 'Send feedback',
        thankYouTitle: 'Thank you for traveling with us! ❤️',
        thankYouBody: `Your feedback has been received and helps us create even better travel experiences.<br><br>🎁 You're entered into the draw for a 100 € travel gift card.<br><br>We hope to see you again soon on a new journey.`,
        requiredIndicator: 'Required',
        textareaPlaceholder: 'Share your thoughts...',
        languageLabel: 'Language',
        requiredBeforeSubmit: 'Please answer all required questions before submitting.',
        submitFailed: 'Unable to submit the survey. Please try again later.',
        requiredBeforeContinue: 'Please answer all required questions before continuing.',
        npsScaleLegend: '0 = not at all likely, 10 = extremely likely',
        privacyNotice:
          'By submitting your feedback, you accept IMT\'s <a href="https://www.imt.fi/rekisteri-ja-tietosuojaseloste/" target="_blank" rel="noopener noreferrer">privacy policy</a>.',
      },
    } as const;

    return texts[locale][key];
  }

  protected getStepLabel(pageId: SurveyPage['id']): string {
    const locale = this.selectedLocale();
    const labels = {
      fi: {
        nps: 'Suosittelu',
        tripReview: 'Arvosanat',
        internalFeedback: 'Avoin palaute',
      },
      en: {
        nps: 'Recommendation',
        tripReview: 'Ratings',
        internalFeedback: 'Open feedback',
      },
    } as const;

    return labels[locale][pageId] ?? '';
  }

  protected shouldShowContextList(section: SurveySection): boolean {
    if (this.isCombinedSameShipSection(section)) {
      return false;
    }

    return !!section.tripPhaseContext?.some((ctx) => this.shouldShowContextItem(section, ctx));
  }

  protected shouldShowContextItem(
    section: SurveySection,
    context: SurveyTripPhaseContext,
  ): boolean {
    if (this.isCombinedSameShipSection(section)) {
      return false;
    }

    if (context.start && context.end) {
      return true;
    }

    // Only show the name if there are multiple context items (to distinguish them)
    // or if the name isn't already used as the section title.
    if (section.tripPhaseContext && section.tripPhaseContext.length > 1) {
      return true;
    }

    return false;
  }

  protected formatTripPhaseContext(context: SurveyTripPhaseContext): string {
    if (context.start && context.end) {
      return `${context.name} (${context.start} -> ${context.end})`;
    }

    return context.name;
  }

  private isCombinedSameShipSection(section: SurveySection): boolean {
    const contexts = section.tripPhaseContext;
    if (!contexts || contexts.length < 2) {
      return false;
    }

    const firstName = contexts[0]?.name;
    return !!firstName && contexts.every((context) => context.name === firstName);
  }

  protected getSectionTitle(section: SurveySection): string {
    const context = section.tripPhaseContext?.[0];
    if (context) {
      return context.name;
    }
    return this.text(section.title);
  }

  protected getSectionSubtitle(section: SurveySection): string {
    const titleFi = this.getSafeText(section.title, 'fi').toLowerCase();
    const titleEn = this.getSafeText(section.title, 'en').toLowerCase();
    const locale = this.selectedLocale();

    const mappings: Record<string, Record<string, string>> = {
      fi: {
        matka: 'Kysymyksiä matkaan liittyen',
        menolaiva: 'Kysymyksiä menolaivaan liittyen',
        laiva: 'Kysymyksiä laivaan liittyen',
        hotelli: 'Kysymyksiä hotelliin liittyen',
        paluulaiva: 'Kysymyksiä paluulaivaan liittyen',
        'imt bussi': 'Kysymyksiä bussiin liittyen',
        lentoyhtiö: 'Kysymyksiä lentoon liittyen',
      },
      en: {
        trip: 'Questions related to the trip',
        'outbound ship': 'Questions related to the outbound ship',
        ship: 'Questions related to the ship',
        hotel: 'Questions related to the hotel',
        'return ship': 'Questions related to the return ship',
        'imt bus': 'Questions related to the bus',
        airline: 'Questions related to the flight',
      },
    };

    const mapping = mappings[locale][locale === 'fi' ? titleFi : titleEn];
    if (mapping) {
      return mapping;
    }

    return this.text(section.description);
  }

  private getSafeText(value: SurveyLocalizedText | string, locale: 'fi' | 'en'): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value[locale] || '';
  }

  protected buildFieldKey(section: SurveySection, questionId: string): string {
    return `${this.getSectionKey(section)}_${questionId}`;
  }

  protected getAnswer(section: SurveySection, questionId: string): SurveyAnswerValue {
    return this.getQuestionControl(section, questionId).value;
  }

  protected setAnswer(
    section: SurveySection,
    question: SurveyQuestion,
    value: SurveyAnswerValue,
  ): void {
    this.getQuestionControl(section, question.id).setValue(
      this.normalizeAnswerValue(question.type, value),
    );
    this.submitError.set(null);
  }

  protected isMissingRequiredQuestion(section: SurveySection, question: SurveyQuestion): boolean {
    if (!question.required || !this.shouldShowQuestion(section, question)) {
      return false;
    }

    const control = this.getQuestionControl(section, question.id);
    return control.touched && this.isAnswerMissing(control.value);
  }

  protected setLocale(locale: 'fi' | 'en'): void {
    this.selectedLocale.set(locale);
  }

  /**
   * Shown at the top of the form, because blocking submit also jumps the customer back to
   * the page holding the unanswered question. Real submit failures render by the button
   * instead -- see submitFailureMessage().
   */
  protected requiredFieldsError(): string | null {
    const currentSubmitError = this.submitError();
    return currentSubmitError === this.uiText('requiredBeforeSubmit') ? currentSubmitError : null;
  }

  protected submitFailureMessage(): string | null {
    const currentSubmitError = this.submitError();
    if (
      currentSubmitError === this.uiText('requiredBeforeContinue') ||
      currentSubmitError === this.uiText('requiredBeforeSubmit')
    ) {
      return null;
    }

    return currentSubmitError;
  }

  protected navigationErrorMessage(): string {
    const currentSubmitError = this.submitError();
    if (currentSubmitError === this.uiText('requiredBeforeContinue')) {
      return currentSubmitError;
    }

    return '';
  }

  protected getQuestionControl(
    section: SurveySection,
    questionId: string,
  ): FormControl<SurveyAnswerValue> {
    const sectionGroup = this.getSectionGroup(section, true);
    const control = sectionGroup.controls[questionId] as FormControl<SurveyAnswerValue> | undefined;
    if (control) {
      return control;
    }

    const fallbackControl = new FormControl<SurveyAnswerValue>(null);
    sectionGroup.addControl(questionId, fallbackControl);
    return fallbackControl;
  }

  private findFirstMissingRequiredQuestion(pages: SurveyPage[]): { pageIndex: number } | null {
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const page = pages[pageIndex];
      for (const section of page.sections) {
        for (const question of section.questions) {
          if (!question.required || !this.shouldShowQuestion(section, question)) {
            continue;
          }

          const answer = this.getAnswer(section, question.id);
          if (this.isAnswerMissing(answer)) {
            return { pageIndex };
          }
        }
      }
    }

    return null;
  }

  private getSubmitErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const payload = error as {
      message?: unknown;
      error?: {
        message?: unknown;
      };
    };

    const nestedMessage = this.resolveErrorMessage(payload.error?.message);
    if (nestedMessage) {
      return nestedMessage;
    }

    if (error instanceof HttpErrorResponse) {
      // HttpErrorResponse.message is always the generic "Http failure response for
      // <url>: 400 Bad Request", which is not something to show a customer. Let the
      // caller fall back to the localised message instead.
      return null;
    }

    return this.resolveErrorMessage(payload.message);
  }

  private resolveErrorMessage(value: unknown): string | null {
    if (typeof value === 'string') {
      const message = value.trim();
      return message.length > 0 ? message : null;
    }

    if (Array.isArray(value)) {
      const messages = value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      if (messages.length > 0) {
        return messages.join(', ');
      }
    }

    return null;
  }

  private getSectionKey(section: SurveySection): string {
    const tripPhaseId = this.getTripPhaseId(section);
    if (!tripPhaseId) {
      return section.id;
    }

    return `${section.id}.tp${tripPhaseId}`;
  }

  private markMissingRequiredQuestionsAsTouched(pages: SurveyPage[]): boolean {
    let hasMissingRequiredQuestions = false;

    for (const page of pages) {
      for (const section of page.sections) {
        for (const question of section.questions) {
          if (!question.required || !this.shouldShowQuestion(section, question)) {
            continue;
          }

          const control = this.getQuestionControl(section, question.id);
          if (!this.isAnswerMissing(control.value)) {
            continue;
          }

          control.markAsTouched();
          control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          hasMissingRequiredQuestions = true;
        }
      }
    }

    return hasMissingRequiredQuestions;
  }

  private isAnswerMissing(answer: SurveyAnswerValue): boolean {
    return answer === null || answer === undefined || answer === '';
  }

  private buildForm(pages: SurveyPage[]): void {
    this.formValueSubscription?.unsubscribe();

    const form = new FormGroup({});
    for (const page of pages) {
      for (const section of page.sections) {
        form.addControl(this.getSectionKey(section), this.buildSectionFormGroup(section));
      }
    }

    this.surveyForm = form;
    this.formValueSubscription = this.surveyForm.valueChanges.subscribe(() => {
      this.clearContinueErrorIfPageIsValid();
    });
  }

  protected getHeroSubtitle(): string {
    const index = this.currentPageIndex();
    const page = this.currentPage();

    // Page 1 (index 0): Show the justification text
    if (index === 0) {
      return this.uiText('heroSubtitleFirstPage');
    }

    // Page 2 and 3 (index 1 and 2): Move descriptions to hero subtitle
    if (page?.description) {
      return this.text(page.description);
    }

    // Fallback or other pages
    return this.uiText('heroSubtitle');
  }

  private clearContinueErrorIfPageIsValid(): void {
    if (this.submitError() !== this.uiText('requiredBeforeContinue')) {
      return;
    }

    const page = this.currentPage();
    const hasMissingRequiredQuestions = page
      ? this.findFirstMissingRequiredQuestion([page]) !== null
      : false;
    if (!hasMissingRequiredQuestions) {
      this.submitError.set(null);
    }
  }

  private buildSectionFormGroup(section: SurveySection): FormGroup {
    const controls: Record<string, FormControl<SurveyAnswerValue>> = {};
    for (const question of section.questions) {
      controls[question.id] = this.buildQuestionControl(question);
    }

    return new FormGroup(controls);
  }

  private buildQuestionControl(question: SurveyQuestion): FormControl<SurveyAnswerValue> {
    const validators = question.required ? [Validators.required] : [];
    return new FormControl<SurveyAnswerValue>(null, { validators });
  }

  private getSectionGroup(section: SurveySection, createIfMissing: boolean): FormGroup {
    const sectionKey = this.getSectionKey(section);
    const existingGroup = (this.surveyForm.controls as Record<string, unknown>)[sectionKey];
    if (existingGroup instanceof FormGroup) {
      return existingGroup;
    }

    if (!createIfMissing) {
      return new FormGroup({});
    }

    const sectionGroup = this.buildSectionFormGroup(section);
    this.surveyForm.addControl(sectionKey, sectionGroup);
    return sectionGroup;
  }

  private getRawAnswer(section: SurveySection, questionId: string): SurveyAnswerValue {
    const sectionGroup = this.getSectionGroup(section, false);
    const control = sectionGroup.controls[questionId] as FormControl<SurveyAnswerValue> | undefined;
    return control ? control.value : null;
  }

  private getTripPhaseId(section: SurveySection): string | null {
    if (section.tripPhaseId === undefined || section.tripPhaseId === null) {
      return null;
    }

    const tripPhaseId = String(section.tripPhaseId).trim();
    return tripPhaseId.length > 0 ? tripPhaseId : null;
  }

  private getPreferredLocale(): 'fi' | 'en' {
    return 'fi';
  }

  private getDefaultScale(questionType: SurveyQuestion['type']): SurveyScale | null {
    if (questionType === 'nps') {
      return { min: 1, max: 10, step: 1 };
    }

    if (questionType === 'star') {
      return { min: 1, max: 5, step: 1 };
    }

    return null;
  }

  private normalizeAnswerValue(
    questionType: SurveyQuestion['type'],
    value: SurveyAnswerValue,
  ): SurveyAnswerValue {
    if ((questionType === 'nps' || questionType === 'star') && typeof value === 'string') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) {
        return numericValue;
      }
    }

    return value;
  }

  private findAnswerByQuestionId(questionId: string): SurveyAnswerValue {
    for (const control of Object.values(this.surveyForm.controls)) {
      if (!(control instanceof FormGroup)) {
        continue;
      }

      const questionControl = control.controls[questionId] as
        | FormControl<SurveyAnswerValue>
        | undefined;
      if (questionControl) {
        return questionControl.value;
      }
    }

    return null;
  }

  private getAnswersForSubmit(): SurveySubmitAnswers {
    const payload: SurveySubmitAnswers = {};
    const tripPhaseSections: SurveyTripPhaseSectionAnswers[] = [];

    for (const page of this.pages()) {
      for (const section of page.sections) {
        const sectionGroup = this.getSectionGroup(section, false);
        const sectionAnswers = sectionGroup.getRawValue() as Record<string, SurveyAnswerValue>;
        if (Object.keys(sectionAnswers).length === 0) {
          continue;
        }

        const filteredSectionAnswers = Object.fromEntries(
          section.questions
            .filter((question) => this.shouldShowQuestion(section, question))
            .map((question) => [question.id, sectionAnswers[question.id]] as const)
            .filter(([, value]) => value !== null && value !== undefined),
        );

        if (Object.keys(filteredSectionAnswers).length === 0) {
          continue;
        }

        const tripPhaseId = this.getTripPhaseId(section);
        if (tripPhaseId) {
          tripPhaseSections.push({
            sectionId: section.id,
            tripPhaseId,
            answers: filteredSectionAnswers,
          });
          continue;
        }

        if (
          section.id === 'nps' ||
          section.id === 'trip_general' ||
          section.id === 'internal_feedback'
        ) {
          const staticSectionId = section.id as 'nps' | 'trip_general' | 'internal_feedback';
          payload[staticSectionId] = filteredSectionAnswers;
        }
      }
    }

    if (tripPhaseSections.length > 0) {
      payload.trip_phase_sections = tripPhaseSections;
    }

    return payload;
  }

  private buildDemoPages(): SurveyPage[] {
    // Keep this demo/fallback schema in sync with apps/modern-backend/src/survey/survey.schema.ts.
    // These labels and rules are duplicated here because demo mode does not fetch backend survey pages.
    return [
      {
        id: 'nps',
        title: {
          fi: '',
          en: '',
        },
        description: {
          fi: '',
          en: '',
        },
        sections: [
          {
            id: 'nps',
            title: { fi: 'Suosittelu', en: 'Recommendation' },
            questions: [
              {
                id: 'nps_score',
                type: 'nps',
                title: {
                  fi: 'Kuinka todennäköistä on, että suosittelisit IMT:tä tuttavillesi?',
                  en: 'On a scale of 1-10, how likely are you to recommend IMT to your friends?',
                },
                description: {
                  fi: '0 = en suosittelisi, 10 = todennäköisin',
                  en: '0 = not at all likely, 10 = extremely likely',
                },
                required: true,
                scale: { min: 1, max: 10, step: 1 },
              },
              {
                id: 'nps_followup_feedback',
                type: 'textarea',
                title: {
                  fi: 'Kerro, mitä voisimme tehdä paremmin.',
                  en: 'Tell us what we could do better.',
                },
                conditional: { whenQuestionId: 'nps_score', operator: 'lte', value: 6 },
              },
              {
                id: 'nps_followup_contact',
                type: 'choice',
                title: {
                  fi: 'Saammeko olla yhteydessä palautteeseesi liittyen?',
                  en: 'May we contact you about your feedback?',
                },
                conditional: { whenQuestionId: 'nps_score', operator: 'lte', value: 6 },
                options: [
                  { value: 'yes', label: { fi: 'Kyllä', en: 'Yes' } },
                  { value: 'no', label: { fi: 'Ei', en: 'No' } },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'tripReview',
        title: { fi: '', en: '' },
        description: {
          fi: `<strong>🌍 Julkinen palauteosio</strong>

Tämän sivun arvostelut julkaistaan muiden matkailijoiden nähtäväksi, mikä auttaa heitä valitsemaan sopivan matkan, hotellin ja kuljetuspalvelut.

Älä kirjoita henkilötietoja, kuten nimiä tai yhteystietoja.`,
          en: `<strong>🌍 Public feedback section</strong>

The reviews on this page will be published for other travelers to see and help them choose a suitable trip, hotel and transport services.

Do not include personal information, such as names or contact details.`,
        },
        sections: [
          {
            id: 'trip_general',
            title: { fi: 'Matka', en: 'Trip' },
            tripPhaseContext: [{ name: 'Yö Tallinnassa' }],
            questions: [
              {
                id: 'trip_general_rating',
                type: 'star',
                title: { fi: 'Matkan kokonaisarvosana', en: 'Overall trip rating' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: true,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'trip_general_arrangements_ease_rating',
                type: 'star',
                title: { fi: 'Matkan vaivattomuus', en: 'Ease of travel' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: true,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'trip_general_value_for_money_rating',
                type: 'star',
                title: { fi: 'Vastinetta rahalle', en: 'Value for money' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: true,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'trip_general_feedback',
                type: 'textarea',
                title: { fi: 'Kommentti', en: 'Comment' },
              },
            ],
          },
          {
            id: 'trip_ship',
            title: { fi: 'Laiva', en: 'Ship' },
            tripPhaseId: 1,
            tripPhaseIndex: 0,
            tripPhaseContext: [
              {
                name: 'Tallink MyStar',
                start: 'Länsiterminaali T2',
                end: 'Tallinnan D-terminaali',
              },
              {
                name: 'Tallink MyStar',
                start: 'Tallinnan D-terminaali',
                end: 'Länsiterminaali T2',
              },
            ],
            questions: [
              {
                id: 'rating',
                type: 'star',
                title: { fi: 'Laivan kokonaisarvosana', en: 'Overall ship rating' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: false,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'feedback',
                type: 'textarea',
                title: { fi: 'Kommentti', en: 'Comment' },
              },
            ],
          },
          {
            id: 'trip_hotel',
            title: { fi: 'Hotelli', en: 'Hotel' },
            tripPhaseId: 2,
            tripPhaseIndex: 1,
            tripPhaseContext: [{ name: 'City Hotel Portus' }],
            questions: [
              {
                id: 'rating',
                type: 'star',
                title: { fi: 'Hotellin kokonaisarvosana', en: 'Overall hotel rating' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: true,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'breakfast_rating',
                type: 'star',
                title: { fi: 'Aamiainen', en: 'Breakfast' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: false,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'spa_rating',
                type: 'star',
                title: { fi: 'Kylpylä (jos oli)', en: 'Spa (if included)' },
                description: { fi: '1 = huonoin, 5 = paras', en: '1 = poorest, 5 = excellent' },
                required: false,
                scale: { min: 1, max: 5, step: 1 },
              },
              {
                id: 'feedback',
                type: 'textarea',
                title: { fi: 'Kommentti', en: 'Comment' },
              },
            ],
          },
        ],
      },
      {
        id: 'internalFeedback',
        title: { fi: '', en: '' },
        description: {
          fi: `<b>Tämän palauteosion vastaukset näkyvät vain IMT:lle</b> ja ne auttavat meitä parantamaan matkoja sekä palveluamme.`,
          en: `<b>The answers in this feedback section are visible only to IMT</b> and they help us improve our trips and our service.`,
        },
        sections: [
          {
            id: 'internal_feedback',
            title: { fi: 'Kokemuksesi', en: 'Your experience' },
            questions: [
              {
                id: 'internal_best_part',
                type: 'textarea',
                title: {
                  fi: 'Mikä oli parasta matkallasi?',
                  en: 'What was the best part of your trip?',
                },
              },
              {
                id: 'internal_worst_part',
                type: 'textarea',
                title: {
                  fi: 'Mikä oli huonointa matkallasi?',
                  en: 'What was the worst part of your trip?',
                },
              },
              {
                id: 'internal_sustainability',
                type: 'textarea',
                title: {
                  fi: 'Kuinka vastuulliseksi koit tämän matkan kokonaisuudessaan (esim. kuljetus, majoitus ja aktiviteetit)?',
                  en: 'How sustainable did you find this trip overall (e.g. transport, accommodation, and activities)?',
                },
              },
            ],
          },
        ],
      },
    ];
  }

  protected goNext() {
    const page = this.currentPage();
    const hasMissingRequiredQuestions = page
      ? this.markMissingRequiredQuestionsAsTouched([page])
      : false;
    if (hasMissingRequiredQuestions) {
      this.submitError.set(this.uiText('requiredBeforeContinue'));
      return;
    }

    this.submitError.set(null);

    const nextIndex = this.currentPageIndex() + 1;
    if (nextIndex < this.pages().length) {
      this.currentPageIndex.set(nextIndex);
      window.scrollTo(0, 0);
    }
  }

  protected goBack() {
    const nextIndex = this.currentPageIndex() - 1;
    if (nextIndex >= 0) {
      this.currentPageIndex.set(nextIndex);
      window.scrollTo(0, 0);
    }
  }
}
